import { useMemo, useState } from 'react';
import {
  todayISO, formatMoney, nightsBetween,
  type HebergementProperty, type HebergementUnit,
  type HebergementBlock, type HebergementReservation,
} from '@/lib/hebergement';

interface ReservePrefill {
  propertyId: number;
  unitId: number | null;
  checkIn: string;
  checkOut: string;
  pricePerNight: number;
}

interface Props {
  properties: HebergementProperty[];
  units: HebergementUnit[];
  blocks: HebergementBlock[];
  reservations: HebergementReservation[];
  currency: string;
  createBlock: (data: { propertyId: number; unitId: number | null; startDate: string; endDate: string; reason?: string }) => Promise<void>;
  deleteBlock: (id: number) => Promise<void>;
  checkAvailability: (propertyId: number, unitId: number | null, checkIn: string, checkOut: string) => Promise<boolean>;
  onReserve: (prefill: ReservePrefill) => void;
}

const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function iso(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export default function CalendarView({
  properties, units, blocks, reservations, currency, createBlock, deleteBlock, checkAvailability, onReserve,
}: Props) {
  const today = todayISO();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [propertyId, setPropertyId] = useState<number | null>(properties[0]?.id ?? null);
  const [unitId, setUnitId] = useState<number | null>(null);
  const [rangeStart, setRangeStart] = useState<string | null>(null);
  const [rangeEnd, setRangeEnd] = useState<string | null>(null);
  const [blockOpen, setBlockOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const property = properties.find((p) => p.id === propertyId) || null;

  const relevantReservations = useMemo(() => {
    return reservations.filter((r) => {
      if (r.property_id !== propertyId) return false;
      if (!['pending', 'confirmed', 'checked_in'].includes(r.status)) return false;
      if (unitId === null) return true;
      return r.unit_id === null || r.unit_id === unitId;
    });
  }, [reservations, propertyId, unitId]);

  const relevantBlocks = useMemo(() => {
    return blocks.filter((b) => {
      if (b.property_id !== propertyId) return false;
      if (unitId === null) return true;
      return b.unit_id === null || b.unit_id === unitId;
    });
  }, [blocks, propertyId, unitId]);

  const reservedDays = useMemo(() => {
    const set = new Set<string>();
    relevantReservations.forEach((r) => {
      let cur = String(r.check_in).slice(0, 10);
      const end = String(r.check_out).slice(0, 10);
      while (cur < end) {
        set.add(cur);
        const d = new Date(`${cur}T00:00:00`); d.setDate(d.getDate() + 1);
        cur = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
    });
    return set;
  }, [relevantReservations]);

  const blockedDays = useMemo(() => {
    const set = new Set<string>();
    relevantBlocks.forEach((b) => {
      let cur = String(b.start_date).slice(0, 10);
      const end = String(b.end_date).slice(0, 10);
      while (cur < end) {
        set.add(cur);
        const d = new Date(`${cur}T00:00:00`); d.setDate(d.getDate() + 1);
        cur = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
    });
    return set;
  }, [relevantBlocks]);

  const days = useMemo(() => {
    const first = new Date(year, month, 1);
    const startOffset = (first.getDay() + 6) % 7; // lundi = 0
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: { date: string | null }[] = [];
    for (let i = 0; i < startOffset; i += 1) cells.push({ date: null });
    for (let d = 1; d <= daysInMonth; d += 1) cells.push({ date: iso(year, month, d) });
    return cells;
  }, [year, month]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); };

  const handleDayClick = (date: string) => {
    setMessage('');
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(date);
      setRangeEnd(null);
      return;
    }
    if (date === rangeStart) { setRangeStart(null); return; }
    if (date < rangeStart) { setRangeEnd(rangeStart); setRangeStart(date); return; }
    setRangeEnd(date);
  };

  const inSelectedRange = (date: string): boolean => {
    if (!rangeStart) return false;
    if (!rangeEnd) return date === rangeStart;
    return date >= rangeStart && date <= rangeEnd;
  };

  const nights = rangeStart && rangeEnd ? nightsBetween(rangeStart, rangeEnd) : 0;
  const pricePerNight = property ? Number(property.price) || 0 : 0;
  const estimatedTotal = pricePerNight * nights;

  const handleCheck = async () => {
    if (!propertyId || !rangeStart || !rangeEnd) return;
    const ok = await checkAvailability(propertyId, unitId, rangeStart, rangeEnd);
    setMessage(ok ? 'Disponible sur ces dates.' : 'Indisponible : conflit avec une réservation ou un blocage.');
  };

  const handleBlock = async () => {
    if (!propertyId || !rangeStart || !rangeEnd) return;
    setSaving(true);
    try {
      await createBlock({ propertyId, unitId, startDate: rangeStart, endDate: rangeEnd, reason: reason.trim() || 'Blocage' });
      setBlockOpen(false);
      setReason('');
      setRangeStart(null);
      setRangeEnd(null);
    } finally {
      setSaving(false);
    }
  };

  if (properties.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-dashed border-background-300/60 rounded-lg text-center">
        <i className="ri-calendar-line text-3xl text-foreground-300 mb-3"></i>
        <p className="text-sm text-foreground-500">Créez d&apos;abord un bien pour gérer son calendrier.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Sélecteurs */}
      <div className="flex flex-col sm:flex-row gap-3">
        <select value={propertyId ?? ''} onChange={(e) => { setPropertyId(Number(e.target.value)); setUnitId(null); }}
          className="px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 cursor-pointer">
          {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={unitId ?? ''} onChange={(e) => setUnitId(e.target.value ? Number(e.target.value) : null)}
          className="px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 cursor-pointer">
          <option value="">Bien entier</option>
          {units.filter((u) => u.property_id === propertyId).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <div className="flex items-center gap-2 ml-auto">
          <span className="flex items-center gap-1.5 text-[11px] text-foreground-500"><span className="w-3 h-3 rounded bg-primary-500"></span>Réservé</span>
          <span className="flex items-center gap-1.5 text-[11px] text-foreground-500"><span className="w-3 h-3 rounded bg-secondary-400"></span>Bloqué</span>
          <span className="flex items-center gap-1.5 text-[11px] text-foreground-500"><span className="w-3 h-3 rounded bg-accent-100 border border-accent-300"></span>Libre</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Calendrier */}
        <div className="lg:col-span-2 bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer">
              <i className="ri-arrow-left-s-line text-foreground-600"></i>
            </button>
            <span className="text-sm font-bold font-heading text-foreground-950">{MONTHS[month]} {year}</span>
            <button onClick={nextMonth} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-foreground-600"></i>
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEKDAYS.map((w) => <div key={w} className="text-center text-[11px] font-semibold text-foreground-400 py-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((cell, i) => {
              if (!cell.date) return <div key={`empty-${i}`} />;
              const d = cell.date;
              const isReserved = reservedDays.has(d);
              const isBlocked = blockedDays.has(d);
              const isSelected = inSelectedRange(d);
              let cls = 'bg-accent-50 text-foreground-700 hover:bg-accent-100';
              if (isReserved) cls = 'bg-primary-500 text-background-50';
              else if (isBlocked) cls = 'bg-secondary-400 text-background-50';
              const ring = d === today ? 'ring-2 ring-foreground-900 ring-offset-1' : '';
              const sel = isSelected ? 'outline outline-2 outline-foreground-900' : '';
              return (
                <button
                  key={d}
                  onClick={() => handleDayClick(d)}
                  className={`aspect-square rounded-md text-xs font-medium flex items-center justify-center transition-all cursor-pointer ${cls} ${ring} ${sel}`}
                >
                  {parseInt(d.slice(8, 10), 10)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Panneau d'action */}
        <div className="space-y-4">
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground-900 mb-3">Sélection</h3>
            {!rangeStart ? (
              <p className="text-xs text-foreground-500">Cliquez une date pour commencer, puis une date de fin.</p>
            ) : (
              <div className="space-y-3">
                <div className="text-xs text-foreground-600">
                  <div>Arrivée : <strong className="text-foreground-900">{rangeStart}</strong></div>
                  <div>Départ : <strong className="text-foreground-900">{rangeEnd || '—'}</strong></div>
                  {nights > 0 && (
                    <div className="mt-2 px-3 py-2 rounded-md bg-background-100">
                      {nights} nuit{nights > 1 ? 's' : ''} · {formatMoney(estimatedTotal, currency)}
                    </div>
                  )}
                </div>

                {message && (
                  <div className={`px-3 py-2 rounded-md text-xs ${
                    message.startsWith('Disponible') ? 'bg-accent-50 text-accent-700' : 'bg-red-50 text-red-600'
                  }`}>{message}</div>
                )}

                {rangeEnd && (
                  <>
                    <button onClick={handleCheck}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-check-double-line"></i>Vérifier la disponibilité
                    </button>
                    <button
                      onClick={() => propertyId && onReserve({ propertyId, unitId, checkIn: rangeStart, checkOut: rangeEnd, pricePerNight })}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-calendar-check-line"></i>Réserver ces dates
                    </button>
                    <button onClick={() => setBlockOpen(true)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-lock-line"></i>Bloquer ces dates
                    </button>
                  </>
                )}
                <button onClick={() => { setRangeStart(null); setRangeEnd(null); setMessage(''); }}
                  className="w-full text-xs text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer">
                  Effacer la sélection
                </button>
              </div>
            )}
          </div>

          {/* Blocages existants */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
              <i className="ri-lock-line text-secondary-500"></i>Blocages ({relevantBlocks.length})
            </h3>
            {relevantBlocks.length === 0 ? (
              <p className="text-xs text-foreground-400">Aucun blocage sur ce bien.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {relevantBlocks.map((b) => (
                  <div key={b.id} className="flex items-center gap-2 px-3 py-2 rounded-md bg-background-100">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-foreground-800 truncate">
                        {String(b.start_date).slice(0, 10)} → {String(b.end_date).slice(0, 10)}
                      </div>
                      <div className="text-[10px] text-foreground-500 truncate">{b.reason || 'Blocage'} · {b.unit_id ? units.find((u) => u.id === b.unit_id)?.name : 'bien entier'}</div>
                    </div>
                    <button onClick={() => deleteBlock(b.id)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer">
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal blocage */}
      {blockOpen && rangeStart && rangeEnd && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setBlockOpen(false)}></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-2xl border border-background-200/70 p-6">
            <h3 className="text-sm font-bold text-foreground-950 mb-1">Bloquer des dates</h3>
            <p className="text-xs text-foreground-500 mb-4">{rangeStart} → {rangeEnd}</p>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Motif</label>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Maintenance, usage perso, rénovation…"
              className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300 mb-5" />
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setBlockOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={handleBlock} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold bg-secondary-500 text-background-50 whitespace-nowrap hover:bg-secondary-600 transition-colors cursor-pointer disabled:opacity-50">
                {saving ? <><i className="ri-loader-4-line animate-spin"></i>…</> : <><i className="ri-lock-line"></i>Bloquer</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}